import { useState, useEffect, useCallback, useMemo } from 'react';
import MiniSearch from 'minisearch';

/**
 * Interface for search index documents
 */
interface SearchDocument {
  /** Unique document identifier */
  id: string;
  /** Document title */
  title: string;
  /** Document content */
  content: string;
  /** Document category */
  category: string;
  /** File path */
  path: string;
  /** Excerpt for display */
  excerpt: string;
  /** Headings from the document */
  headings?: string;
}

/**
 * Interface for search results
 */
export interface SearchResult {
  /** Document ID */
  id: string;
  /** Document title */
  title: string;
  /** Document category */
  category: string;
  /** File path */
  path: string;
  /** Excerpt for display */
  excerpt: string;
  /** Search score */
  score: number;
  /** Matched terms */
  terms: string[];
}

/**
 * Interface for the hook return value
 */
interface UseDocSearchReturn {
  /** Search function */
  search: (query: string) => SearchResult[];
  /** Whether the search index is loading */
  isLoading: boolean;
  /** Error state */
  error: Error | null;
  /** Whether the search index is ready */
  isReady: boolean;
}

/**
 * Custom hook for searching documentation using MiniSearch
 *
 * @description
 * This hook loads the pre-built search index from `/src/data/search-index.json`
 * and provides a search function for finding relevant documentation.
 *
 * Features:
 * - Full-text search across all documentation
 * - Fuzzy matching for typo tolerance
 * - Prefix search for auto-complete
 * - Searches across title, content, and headings
 * - Returns ranked results with scores
 *
 * The search index is loaded once on mount and cached in memory.
 *
 * @returns An object containing the search function and loading state
 *
 * @example
 * ```tsx
 * function SearchComponent() {
 *   const { search, isLoading, isReady } = useDocSearch();
 *   const [query, setQuery] = useState('');
 *
 *   const results = useMemo(() => {
 *     if (!query.trim() || !isReady) return [];
 *     return search(query);
 *   }, [query, search, isReady]);
 *
 *   if (isLoading) return <div>Loading search index...</div>;
 *
 *   return (
 *     <div>
 *       <input
 *         value={query}
 *         onChange={(e) => setQuery(e.target.value)}
 *         placeholder="Search documentation..."
 *       />
 *       {results.map(result => (
 *         <SearchResultItem key={result.id} result={result} />
 *       ))}
 *     </div>
 *   );
 * }
 * ```
 */
export function useDocSearch(): UseDocSearchReturn {
  const [miniSearch, setMiniSearch] = useState<MiniSearch<SearchDocument> | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  // Load and initialize the search index
  useEffect(() => {
    let mounted = true;

    async function loadSearchIndex() {
      try {
        setIsLoading(true);
        setError(null);

        // Import the search index
        const searchIndexModule = await import('@/data/search-index.json');
        const searchIndexData = searchIndexModule.default as {
          index: Record<string, unknown>;
          documents: SearchDocument[];
        };

        if (!mounted) return;

        // Load the MiniSearch index from the pre-built JSON
        const ms = MiniSearch.loadJSON<SearchDocument>(JSON.stringify(searchIndexData.index), {
          fields: ['title', 'content', 'headings'], // Fields to index
          storeFields: ['title', 'category', 'path', 'excerpt'], // Fields to return in results
          searchOptions: {
            boost: { title: 3, headings: 2 }, // Boost title and headings
            fuzzy: 0.2, // Allow some typos
            prefix: true, // Enable prefix search
          },
        });

        if (mounted) {
          setMiniSearch(ms);
          setIsLoading(false);
        }
      } catch (err) {
        if (mounted) {
          const loadError = err instanceof Error ? err : new Error('Failed to load search index');
          setError(loadError);
          setIsLoading(false);
          // Error is stored in state, no need to log
        }
      }
    }

    loadSearchIndex();

    return () => {
      mounted = false;
    };
  }, []);

  /**
   * Search function that queries the MiniSearch index
   */
  const search = useCallback(
    (query: string): SearchResult[] => {
      if (!miniSearch || !query.trim()) {
        return [];
      }

      try {
        const results = miniSearch.search(query, {
          boost: { title: 3, headings: 2 },
          fuzzy: 0.2,
          prefix: true,
        });

        // Transform results to our SearchResult interface
        return results.map((result) => ({
          id: result.id,
          title: result.title,
          category: result.category,
          path: result.path,
          excerpt: result.excerpt,
          score: result.score,
          terms: result.terms || [],
        }));
      } catch {
        // Return empty array on search error
        return [];
      }
    },
    [miniSearch],
  );

  const isReady = useMemo(
    () => !isLoading && !error && !!miniSearch,
    [isLoading, error, miniSearch],
  );

  return {
    search,
    isLoading,
    error,
    isReady,
  };
}
